import type { UserAuth } from '@repo/common';
import type { SyncDirection, SyncItemsInput } from '@repo/common/types/AdapterSync';
import type { ConversationModel } from '@repo/common/types/Conversation';
import type { MessageModel } from '@repo/common/types/Message';
import type { StoredCalendarEventModel } from '@repo/common/types/StoredCalendarEvent';
import type { TaskModel } from '@repo/common/types/Task';
import type { TranscriptModel } from '@repo/common/types/Transcript';
import type { WorkspaceModel } from '@repo/common/types/Workspace';
import type { AssetModel } from '@repo/common/types/Asset';
import { sendToMultipleTables } from '../data/adapter.data';
import type { ClientDatabaseAdapter } from './base';
import {
  TABLE_NAMES_IN_SYNC_ORDER,
  createClientAdapter,
  getValidTableNames
} from './clientAdapterTables';

export type SyncItemOptions = {
  direction?: SyncDirection;
  onProgress?: (operation: string, itemsComplete: number, totalItems: number) => void;
  adapterConfig?: Partial<{
    baseUrl: string;
    authToken: string;
    timeout: number;
    retries: number;
  }>;
};

/**
 * Internal sync function. Use syncItems for public API.
 */
export async function _syncItems(
  items: SyncItemsInput,
  clientAdapters: Map<string, ClientDatabaseAdapter<any, any>>,
  direction: SyncDirection = 'bidirectional',
  onProgress?: (operation: string, itemsComplete: number, totalItems: number) => void
): Promise<void> {
  const tableNamesWithItems = Object.keys(items).filter(
    (tableName) => items[tableName] && items[tableName].length > 0
  );

  const totalTables = tableNamesWithItems.length;
  let completedTables = 0;

  if (onProgress) {
    onProgress('Starting targeted sync...', 0, totalTables);
  }

  // Sync tables in dependency order
  for (const tableName of TABLE_NAMES_IN_SYNC_ORDER) {
    if (!tableNamesWithItems.includes(tableName)) {
      continue; // Skip tables that don't have items to sync
    }

    try {
      const itemsWithIds = items[tableName].filter((item: any) => !!item.id);
      if (!itemsWithIds || itemsWithIds.length === 0) {
        continue;
      }

      // Try client adapter first (for client-side operations)
      const clientAdapter = clientAdapters.get(tableName);
      if (clientAdapter) {
        await clientAdapter.syncItems({ [tableName]: itemsWithIds }, direction as any, onProgress);
        completedTables++;
        if (onProgress) {
          onProgress(`Synced ${tableName}...`, completedTables, totalTables);
        }
        continue;
      }

      console.warn(`No adapter found for table: ${tableName}`);
    } catch (error) {
      console.debug(`Error syncing table ${tableName}:`, error);
      // Continue with other tables even if one fails
    }
  }

  if (onProgress) {
    onProgress('Targeted sync completed', totalTables, totalTables);
  }
}

export async function commonToPostgres(
  items: SyncItemsInput,
  _userId: string,
  authToken: Pick<UserAuth, 'idToken' | 'refreshToken'>
): Promise<void> {
  sendToMultipleTables(items, authToken);
}
/**
 * Public sync function. Instantiates adapters from adapterTables.js.
 */
export async function syncItems(
  items: SyncItemsInput,
  models: {
    workspaceModel: WorkspaceModel;
    taskModel: TaskModel;
    transcriptModel: TranscriptModel;
    messageModel: MessageModel;
    conversationModel: ConversationModel;
    calendarEventModel: StoredCalendarEventModel;
    assetModel: AssetModel;
  },
  userId: string,
  authToken: Pick<UserAuth, 'idToken' | 'refreshToken'>,
  options?: SyncItemOptions
): Promise<void> {
  const clientAdapters = new Map<string, ClientDatabaseAdapter<any, any>>();
  const validTableNames = getValidTableNames();

  for (const tableName of validTableNames) {
    try {
      const clientAdapter = createClientAdapter(
        tableName,
        models,
        userId,
        authToken,
        options?.adapterConfig
      );
      if (clientAdapter) {
        clientAdapters.set(tableName, clientAdapter);
      }
    } catch {
      // Ignore errors if client adapter can't be created for this table
    }
  }
  return _syncItems(items, clientAdapters, options?.direction, options?.onProgress);
}
